import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { curveMonotoneX, line } from 'd3-shape';
import { scaleLinear } from 'd3-scale';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import {
  formatMilestoneYears,
  formatWealthAmount,
  type CompoundYearRow,
  type WealthMilestone,
  type WealthMilestonePlan,
} from '../../utils/investmentCompounding';
import {
  ChartHScroll,
  formatK,
  MIN_PX_PER_YEAR,
  useChartPlotWidth,
} from './d3';
import { WealthMilestoneLegend } from './WealthMilestoneLegend';
import { SvgChartCanvas } from './web/SvgChartCanvas.web';

type Props = {
  plan: WealthMilestonePlan;
  annualRatePct: number;
  annualContribution: number;
  height?: number;
};

const MARGIN = { top: 18, right: 14, bottom: 28, left: 44 };
const MILESTONE_SEGMENT_COLORS = [
  '#38BDF8',
  '#F87171',
  '#FBBF24',
  '#A78BFA',
  '#3DD6A4',
  '#FB923C',
  '#22D3EE',
  '#E879F9',
  '#F472B6',
  '#4ADE80',
];

function segmentColor(index: number): string {
  return MILESTONE_SEGMENT_COLORS[index % MILESTONE_SEGMENT_COLORS.length]!;
}

type ChartPoint = { x: number; y: number };

/** Yearly samples capped at the exact target crossing — never past totalYears / target. */
function buildCappedPoints(
  rows: CompoundYearRow[],
  target: number,
  endYears: number | null,
): ChartPoint[] {
  const points: ChartPoint[] = [];
  for (const r of rows) {
    if (endYears != null && r.yearIndex > endYears) break;
    points.push({
      x: r.yearIndex,
      y: Math.min(r.endBalance, target),
    });
  }
  if (endYears != null && points.length > 0) {
    while (points.length > 0 && points[points.length - 1]!.x > endYears) {
      points.pop();
    }
    const last = points[points.length - 1];
    if (!last || last.x < endYears - 1e-6) {
      points.push({ x: endYears, y: target });
    } else {
      points[points.length - 1] = { x: endYears, y: target };
    }
  }
  return points;
}

/** Colored segments that share exact milestone endpoints (no gaps, no overshoot). */
function buildColoredSegments(
  points: ChartPoint[],
  milestones: WealthMilestone[],
  startBalance: number,
): { points: ChartPoint[]; color: string }[] {
  if (points.length === 0) return [];
  if (milestones.length === 0) {
    return [{ points, color: segmentColor(0) }];
  }

  const out: { points: ChartPoint[]; color: string }[] = [];
  let prevX = 0;
  let prevY = Math.min(startBalance, milestones[0]?.amount ?? startBalance);

  milestones.forEach((m, i) => {
    const seg: ChartPoint[] = [{ x: prevX, y: prevY }];
    for (const p of points) {
      if (p.x > prevX + 1e-9 && p.x < m.cumulativeYears - 1e-9) {
        seg.push(p);
      }
    }
    seg.push({ x: m.cumulativeYears, y: m.amount });
    if (seg.length >= 2) {
      out.push({ points: seg, color: segmentColor(i) });
    }
    prevX = m.cumulativeYears;
    prevY = m.amount;
  });

  return out;
}

/** Every whole year on the axis up to the domain end — no skipping. */
function buildXTicks(domainMax: number): number[] {
  const end = Math.max(0, Math.floor(domainMax));
  return Array.from({ length: end + 1 }, (_, i) => i);
}

export function WealthMilestoneChart({
  plan,
  annualRatePct,
  annualContribution,
  height = 280,
}: Props) {
  const rows = plan.rows;
  const totalYears = plan.totalYears;
  const domainMax =
    plan.reached && totalYears != null && totalYears > 0
      ? totalYears
      : rows.length > 0
        ? Math.max(...rows.map((r) => r.yearIndex), 1)
        : 1;
  const { plotWidth: width, scrolls, onLayout } = useChartPlotWidth({
    unitCount: Math.max(domainMax, 1),
    minPxPerUnit: MIN_PX_PER_YEAR,
    leftMargin: MARGIN.left,
    rightMargin: MARGIN.right,
  });
  const maxY = plan.target;
  const startBalance = rows[0]?.endBalance ?? 0;

  const model = useMemo(() => {
    if (rows.length === 0 || width <= 0) {
      return {
        paths: [] as { d: string; color: string }[],
        yTicks: [] as { value: number; y: number }[],
        xTicks: [] as { year: number; x: number }[],
        markers: [] as {
          x: number;
          prevX: number;
          barWidth: number;
          y: number;
          color: string;
          yearsLabel: string;
          amountLabel: string;
        }[],
        remainToX: null as number | null,
      };
    }

    const innerWidth = width - MARGIN.left - MARGIN.right;
    const innerHeight = height - MARGIN.top - MARGIN.bottom;
    const xScale = scaleLinear()
      .domain([0, Math.max(domainMax, 1)])
      .range([MARGIN.left, MARGIN.left + Math.max(innerWidth, 1)]);
    const yScale = scaleLinear()
      .domain([0, maxY])
      .nice()
      .range([MARGIN.top + Math.max(innerHeight, 1), MARGIN.top]);

    const lineGen = line<ChartPoint>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y))
      .curve(curveMonotoneX);

    const capped = buildCappedPoints(
      rows,
      plan.target,
      plan.reached ? totalYears : null,
    );
    const colored = buildColoredSegments(capped, plan.milestones, startBalance);
    let prevX = xScale(0);
    const markers = plan.milestones.map((m, i) => {
      const x = xScale(m.cumulativeYears);
      const item = {
        x,
        prevX,
        barWidth: x - prevX,
        y: yScale(Math.min(m.amount, maxY)),
        color: segmentColor(i),
        yearsLabel: formatMilestoneYears(m.yearsFromPrevious),
        amountLabel: formatWealthAmount(m.amount),
      };
      prevX = x;
      return item;
    });

    return {
      paths: colored.map((seg) => ({
        d: lineGen(seg.points) ?? '',
        color: seg.color,
      })),
      yTicks: yScale.ticks(4).map((value) => ({ value, y: yScale(value) })),
      xTicks: buildXTicks(domainMax).map((year) => ({
        year,
        x: xScale(year),
      })),
      markers,
      remainToX: plan.reached ? null : xScale(domainMax),
    };
  }, [
    rows,
    width,
    height,
    domainMax,
    maxY,
    plan.milestones,
    plan.target,
    plan.reached,
    totalYears,
    startBalance,
  ]);

  const maxYearLabel = Math.floor(domainMax);

  if (plan.alreadyPastTarget) {
    return (
      <View style={styles.empty}>
        <Text style={styles.emptyTitle}>You’re already past {formatWealthAmount(plan.target)}</Text>
        <Text style={styles.emptyBody}>Raise the target to see the next stretch of the climb.</Text>
      </View>
    );
  }

  if (rows.length <= 1) {
    return (
      <View style={styles.empty}>
        <Text style={styles.emptyTitle}>Add a starting balance or yearly contribution</Text>
        <Text style={styles.emptyBody}>
          We’ll project how long each {formatWealthAmount(plan.step)} takes.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      <View style={styles.totalBanner}>
        <Text style={styles.totalBannerLabel}>
          {plan.reached && totalYears != null
            ? `Total time to ${formatWealthAmount(plan.target)}`
            : `Toward ${formatWealthAmount(plan.target)}`}
        </Text>
        <Text style={styles.totalBannerValue}>
          {plan.reached && totalYears != null
            ? `${formatMilestoneYears(totalYears)} years`
            : `${maxYearLabel}+ years`}
        </Text>
      </View>

      <ChartHScroll
        height={height}
        plotWidth={width}
        scrolls={scrolls}
        onLayout={onLayout}
        style={styles.chartArea}
        axisWidth={MARGIN.left}
        axis={model.yTicks.map((tick, i) => (
          <Text
            key={`yl-${i}`}
            style={[styles.yLabel, { top: tick.y - 7 }]}
            pointerEvents="none"
          >
            {formatK(tick.value)}
          </Text>
        ))}
        footer={
          model.markers.length > 0 ? (
            <WealthMilestoneLegend
              width={width}
              items={model.markers}
              remainToX={model.remainToX}
            />
          ) : null
        }
      >
        {width > 0 && (
          <SvgChartCanvas width={width} height={height}>
            {model.yTicks.map((tick, i) => (
              <line
                key={`yg-${i}`}
                x1={MARGIN.left}
                y1={tick.y}
                x2={width - MARGIN.right}
                y2={tick.y}
                stroke={colors.border}
                strokeWidth={1}
                opacity={0.4}
              />
            ))}
            {model.paths.map((p, i) => (
              <path
                key={`path-${i}`}
                d={p.d}
                fill="none"
                stroke={p.color}
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
            {model.markers.map((m, i) => (
              <g key={`m-${i}`}>
                <line
                  x1={m.x}
                  y1={MARGIN.top}
                  x2={m.x}
                  y2={height - MARGIN.bottom}
                  stroke={m.color}
                  strokeWidth={1}
                  opacity={0.35}
                />
                <circle cx={m.x} cy={m.y} r={4.5} fill={m.color} />
              </g>
            ))}
            {model.markers.map((m, i) => (
              <text
                key={`ml-${i}`}
                x={m.x}
                y={Math.max(m.y - 10, 12)}
                textAnchor="middle"
                fill={m.color}
                fontSize={11}
                fontWeight={700}
              >
                {m.yearsLabel}
              </text>
            ))}
            {model.xTicks.map((tick, i) => (
              <text
                key={`xl-${i}`}
                x={tick.x}
                y={height - 6}
                textAnchor="middle"
                fill={colors.textMuted}
                fontSize={domainMax > 25 ? 9 : 10}
                fontWeight={600}
              >
                {tick.year}
              </text>
            ))}
          </SvgChartCanvas>
        )}
      </ChartHScroll>
      <Text style={styles.xAxisCaption}>Years</Text>

      {plan.insights.length > 0 && plan.reached && (
        <View style={styles.insightRow}>
          {plan.insights.map((insight) => (
            <View key={insight.amount} style={styles.insightChip}>
              <Text style={styles.insightText}>
                {formatWealthAmount(insight.amount)} is {insight.timeSharePct}% of the way to{' '}
                {formatWealthAmount(plan.target)}
              </Text>
            </View>
          ))}
        </View>
      )}

      <Text style={styles.assumptions}>
        Assumes {formatWealthAmount(annualContribution)}/yr invested · {annualRatePct.toFixed(1)}%
        annualized return
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
  },
  totalBanner: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing.sm,
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.28)',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  totalBannerLabel: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
    flex: 1,
  },
  totalBannerValue: {
    ...typography.headline,
    color: colors.accent,
    fontSize: 20,
    fontVariant: ['tabular-nums'],
  },
  chartArea: {
    width: '100%',
  },
  yLabel: {
    position: 'absolute',
    left: 0,
    width: 40,
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 10,
    textAlign: 'right',
  },
  xAxisCaption: {
    ...typography.caption,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: -4,
    fontSize: 10,
  },
  insightRow: {
    gap: spacing.xs,
  },
  insightChip: {
    backgroundColor: 'rgba(61, 214, 164, 0.1)',
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.28)',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  insightText: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  assumptions: {
    ...typography.caption,
    color: colors.textMuted,
  },
  empty: {
    padding: spacing.md,
    gap: 4,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  emptyTitle: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  emptyBody: {
    ...typography.caption,
    color: colors.textMuted,
  },
});
